import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, Users } from "lucide-react";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { client } from "@/lib/api";
import SignInPrompt from "@/pages/member/SignInPrompt";
import { CompactCard } from "@/pages/member/feedCard";

/**
 * Wave 15 Step 6 (Vania 3 Oct 2026) · Yap thread view.
 *
 * Route: /app/yap/:threadId  (threadId can be "squad:<squad_id>" or a DM uuid)
 *
 *  · Loads messages via GET /api/yap/threads/:id/messages
 *  · Bumps read cursor on mount via POST /api/yap/threads/:id/read
 *  · Composer sends text messages (POST /api/yap/threads/:id/messages, kind=text)
 *  · event_share + event_invite messages render as inline cards. Accepting an
 *    event_invite happens from the regular `/invites` inbox (and backend-side
 *    appends-on-accept per Step 2).
 */
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";
const YELLOW = "#FFD60A";

export default function ThreadView() {
  const { threadId: raw } = useParams();
  const threadId = decodeURIComponent(raw || "");
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [header, setHeader] = useState(null); // {title, kind, squadId?}
  const bottomRef = useRef(null);

  // Resolve a header title from the thread list.
  useEffect(() => {
    if (!user) return;
    client.get("/yap/threads").then((r) => {
      const t = (r.data.threads || []).find((x) => x.id === threadId);
      if (!t) {
        setHeader({ title: threadId.startsWith("squad:") ? "squad" : "dm",
                     kind: threadId.startsWith("squad:") ? "squad" : "dm",
                     squadId: threadId.startsWith("squad:") ? threadId.split("squad:", 2)[1] : null });
        return;
      }
      setHeader({
        title: t.kind === "squad"
          ? (t.squad?.name || "squad").toLowerCase()
          : "@" + (t.other_user?.handle || "someone").toLowerCase(),
        kind: t.kind,
        squadId: t.kind === "squad" ? threadId.split("squad:", 2)[1] : null,
      });
    }).catch(() => {});
  }, [user, threadId]);

  const load = async () => {
    const r = await client.get(`/yap/threads/${encodeURIComponent(threadId)}/messages`);
    setMessages(r.data.messages || []);
    // Fire-and-forget read cursor update.
    client.post(`/yap/threads/${encodeURIComponent(threadId)}/read`).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    load().catch(() => setMessages([]));
    // Poll · 6s · simple + works. SSE/WebSocket is a Step 7+ concern.
    const t = setInterval(() => { if (document.visibilityState === "visible") load().catch(() => {}); }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, threadId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages?.length]);

  if (!authLoading && !user) {
    return <SignInPrompt title="yap"
                         line="sign in to open the thread. we'll bring you back here after."
                         next={`/app/yap/${encodeURIComponent(threadId)}`} testId="yap-signin" />;
  }

  const send = async () => {
    const text = (draft || "").trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const r = await client.post(
        `/yap/threads/${encodeURIComponent(threadId)}/messages`,
        { kind: "text", text });
      setMessages((xs) => [...(xs || []), r.data.message]);
      setDraft("");
    } catch { /* leave the draft so the member can retry */ }
    finally { setSending(false); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="yap-thread"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title={`${header?.title || "yap"} · watu wako`} />
      <header className="flex items-center gap-3 px-4 py-3"
              style={{ borderBottom: `1px solid ${LINE}`, background: "var(--ww-ground)" }}>
        <button onClick={() => nav("/app/squad")} aria-label="back"
                data-testid="yap-back"
                style={{ background: "transparent", border: 0, color: "var(--ww-text)",
                         cursor: "pointer", display: "flex", alignItems: "center" }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16,
                         textTransform: "lowercase" }} data-testid="yap-title">
            {header?.title || "yap"}
          </div>
          {header?.kind === "squad" && header.squadId && (
            <Link to={`/app/squad/${header.squadId}`}
                  data-testid="yap-open-squad"
                  style={{ fontFamily: "Arial", color: META, fontSize: 12,
                           textDecoration: "none" }}>
              open squad
            </Link>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4"
           style={{ overscrollBehavior: "contain" }} data-testid="yap-stream">
        {messages === null ? (
          <p style={{ color: META, fontFamily: "Arial", fontSize: 13 }}>loading…</p>
        ) : messages.length === 0 ? (
          <p style={{ color: META, fontFamily: "Arial", fontSize: 13, textAlign: "center", marginTop: 40 }}>
            no messages yet. say hi.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {groupMessages(messages).map((group) => (
              <MessageGroup key={group.key} group={group}
                            mine={group.author_user_id === user?.user_id} />
            ))}
          </ul>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ borderTop: `1px solid ${LINE}`, padding: 10, background: "var(--ww-ground)" }}>
        <div className="flex items-center gap-2">
          <input value={draft} onChange={(e) => setDraft(e.target.value)}
                 placeholder="type a message"
                 onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                 data-testid="yap-input"
                 style={{ flex: 1, padding: "12px 14px", borderRadius: 999,
                          background: "var(--ww-sunken)", border: `1px solid ${LINE}`,
                          color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none" }} />
          <button onClick={send} disabled={!draft.trim() || sending}
                  data-testid="yap-send" aria-label="send"
                  style={{ width: 44, height: 44, borderRadius: 999,
                           background: draft.trim() ? YELLOW : CARD,
                           color: draft.trim() ? "#1A1A30" : META,
                           border: `1px solid ${draft.trim() ? YELLOW : LINE}`,
                           display: "grid", placeItems: "center",
                           cursor: draft.trim() ? "pointer" : "not-allowed" }}>
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * W-C.29 (Vania 7 Oct 2026) · group consecutive messages from the same person
 * under one name/avatar. event_share messages break their own group so each
 * event card reads cleanly on its own line.
 */
function groupMessages(msgs) {
  // W-C.29 round 2 (Vania 7 Oct) · consecutive messages from the same person
  // group under ONE name + avatar REGARDLESS of kind. Previously event_share
  // broke its own group, so lukas's share + next text showed his name twice.
  const out = [];
  let cur = null;
  for (const m of msgs || []) {
    if (!cur || cur.author_user_id !== m.author_user_id) {
      cur = {
        key: m.id,
        author_user_id: m.author_user_id,
        author_handle: m.author_handle || m.author?.handle || "",
        author_name:   m.author_name   || m.author?.display_name || m.author?.name || "",
        author_photo:  m.author_photo  || m.author?.picture      || m.author?.photo_url || "",
        msgs: [m],
      };
      out.push(cur);
    } else {
      cur.msgs.push(m);
    }
  }
  return out;
}

function MessageGroup({ group, mine }) {
  const align = mine ? "flex-end" : "flex-start";
  const label = (group.author_name || group.author_handle || "someone").toLowerCase();
  const initial = ((label || "?")[0] || "?").toLowerCase();
  return (
    <li style={{ display: "flex", gap: 8, alignItems: "flex-end",
                  justifyContent: align, maxWidth: "100%" }}>
      {!mine && (
        group.author_photo ? (
          <img data-testid="yap-msg-avatar" alt=""
               src={group.author_photo}
               style={{ width: 28, height: 28, borderRadius: 999, objectFit: "cover",
                        flexShrink: 0, background: "var(--ww-sunken)" }} />
        ) : (
          <span data-testid="yap-msg-avatar"
                style={{ width: 28, height: 28, borderRadius: 999,
                          background: "var(--ww-sunken)", color: "var(--ww-text)",
                          display: "grid", placeItems: "center",
                          fontFamily: "Poppins", fontWeight: 800, fontSize: 12,
                          flexShrink: 0 }}>
            {initial}
          </span>
        )
      )}
      <div style={{ display: "flex", flexDirection: "column",
                     alignItems: mine ? "flex-end" : "flex-start",
                     gap: 2, maxWidth: "80%", minWidth: 0 }}>
        {!mine && (
          <span data-testid="yap-msg-name"
                style={{ fontFamily: "Arial", fontSize: 11, color: META,
                          padding: "0 4px" }}>
            {label}
          </span>
        )}
        {group.msgs.map((m) => <MessageBubble key={m.id} m={m} mine={mine} />)}
      </div>
    </li>
  );
}

function MessageBubble({ m, mine }) {
  // W-C.29 (Vania 7 Oct 2026) · own bubble is kili yellow on cream + ink
  // text; on night it switches to the cream fill so the bubble doesn't
  // scream against the deep ground. Others keep the surface fill.
  const isCream = typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "cream";
  const ownBg = isCream ? YELLOW : "#FBF4E9";
  const bg = mine ? ownBg : CARD;
  const fg = mine ? "#1A1A30" : "var(--ww-text)";

  if (m.kind === "event_share" || m.kind === "event_invite") {
    // W-C.29 · a shared event renders as the REAL compact event card (same
    // as L2.3 in the intro). Taps go to the event page. If the event payload
    // isn't hydrated yet, fall back to a plain tap-through row.
    if (m.event && m.event.id) {
      return (
        <div data-testid="yap-event-card"
             style={{ width: "100%", maxWidth: 340 }}
             onClick={(e) => { if (e.target.closest("a")) return; }}>
          <Link to={`/app/event/${m.event.id}`}
                data-testid={`yap-event-card-link-${m.event.id}`}
                style={{ textDecoration: "none", color: "inherit" }}>
            <CompactCard event={m.event} />
          </Link>
          {m.kind === "event_invite" && (
            <p style={{ fontFamily: "Arial", fontSize: 11, color: META,
                        margin: "4px 2px 0" }}>
              private invite · check your invites inbox
            </p>
          )}
        </div>
      );
    }
    return (
      <Link data-testid="yap-event-card"
            to={`/app/event/${m.shared_event_id}`}
            style={{ display: "flex", alignItems: "center", gap: 10,
                     padding: "10px 12px", borderRadius: 14,
                     background: CARD, border: `1px solid ${LINE}`,
                     color: "var(--ww-text)", textDecoration: "none",
                     maxWidth: 300 }}>
        <Users size={18} style={{ color: YELLOW }} />
        <span style={{ fontFamily: "Arial", fontSize: 12, color: META }}>
          {m.text || "tap to open"}
        </span>
      </Link>
    );
  }
  return (
    <div data-testid="yap-msg-text"
         style={{ background: bg, color: fg, padding: "8px 12px",
                   borderRadius: 14, fontFamily: "Arial", fontSize: 14,
                   whiteSpace: "pre-wrap", wordBreak: "break-word",
                   border: mine ? "0" : `1px solid ${LINE}` }}>
      {m.text}
    </div>
  );
}
